/**
 * 4975 — app-wide project-chat notifications for the PWA.
 *
 * Mixed into App.vue so it runs on EVERY screen: a message sent by a colleague
 * shows up whether the user is on the dashboard, a checklist, a weld log or
 * anywhere else.
 *
 * Two delivery paths:
 *
 *   1. Websocket (via $projectChatRt; hosted Pusher or self-hosted, the server
 *      decides at runtime) — arrives immediately.
 *   2. HTTP fetch of project-chat/unread — ON DEMAND ONLY. It seeds the
 *      notifier on launch (riding along on check_and_get_tenant_features, so
 *      no extra request), re-syncs once when the socket (re)connects, and
 *      refreshes when the user comes back to look: dashboard opened, app back
 *      in the foreground — throttled to one request per MIN_GAP_MS.
 *
 * Tenant switch: the seed and every unread answer carry `enabled`. On
 * `enabled: false` the notifier goes dormant — drops its subscriptions, closes
 * the socket and makes no chat request of any kind (no unread fetch, no
 * socket-config, no channel auth) until a later seed says enabled again. Until
 * the first seed arrives it is dormant as well: nothing chat-related runs on a
 * tenant that has not been confirmed to have chat switched on.
 *
 * There is deliberately NO timer, live socket or not. The previous fallback
 * interval (2 s while the socket was down) meant every tenant with realtime
 * switched off, and every user whose socket never came up, hammered
 * project-chat/unread for as long as the app was open. Without a socket chat
 * notifications now simply arrive at the next of those moments instead of
 * within seconds; the chat screen itself has its own refresh while open.
 *
 * The fetch is cheap by design — it sends the version it already holds and the
 * server answers ~30 bytes when nothing has changed.
 */
import { hostAppApi } from '../plugins/axios_settings'

// Minimum gap between two on-demand fetches triggered by the user "looking
// again" (dashboard opened, app foregrounded). A login lands on the dashboard
// right after the launch seed, for example, and must not fetch the same answer
// twice.
const MIN_GAP_MS = 15000
const SEEN_KEY = 'WelditPwa_projectChatSeen'

export default {
  data () {
    return {
      pcnVersion: null,
      // null = no seed yet, false = chat is off for this tenant, true = on.
      // Every fetch, socket nudge and subscription is gated on `true`.
      pcnEnabled: null,
      // When the summary was last fetched or seeded; throttles pcnPollIfStale().
      pcnLastPollAt: 0,
      // project_id -> highest message id already announced, so the same message
      // is never notified twice. Persisted because the PWA remounts on reload.
      pcnSeen: {},
      // Seeded on the first successful poll and NOT announced: a backlog that
      // predates the user opening the app is old news, and a stack of toasts on
      // launch is noise.
      pcnSeeded: false,
      pcnThreadMeta: {},
      // thread id -> off() from joinThread(). One registration per thread: the
      // registry appends, so registering again would toast every message twice.
      pcnOff: {},
      // Unsubscribe handle for the realtime transport-change listener.
      pcnOffTransport: null
    }
  },

  methods: {
    /* ---------------------------------------------------------------- */
    /* Persistence                                                       */
    /* ---------------------------------------------------------------- */

    pcnLoadSeen () {
      try {
        const raw = window.localStorage.getItem(SEEN_KEY)
        this.pcnSeen = raw ? JSON.parse(raw) : {}
        this.pcnSeeded = raw !== null
      } catch (e) {
        this.pcnSeen = {}
        this.pcnSeeded = false
      }
    },

    pcnSaveSeen () {
      try {
        window.localStorage.setItem(SEEN_KEY, JSON.stringify(this.pcnSeen))
      } catch (e) { /* quota / private mode — degrade to in-memory */ }
    },

    /* ---------------------------------------------------------------- */
    /* Notification                                                      */
    /* ---------------------------------------------------------------- */

    /**
     * Show one notification. Uses the app's existing global snackbar so it
     * looks like every other message in the PWA, and makes it tappable by
     * routing to the project's chat.
     */
    pcnNotify (row) {
      const user = row.user || {}
      const who = user.name || ''
      const body = row.body || ''

      this.$root.$emit('snackbar', {
        snackbar: true,
        color: 'info',
        isException: true,
        text: `${row.project_name} — ${who}: ${body}`,
        // Gives the toast an "Open" action that lands on this conversation,
        // matching the web app's clickable toasts. App.vue handles the rest.
        route: `/projects/${row.project_id}/chat`
      })

      // Also raise a real OS notification when the app is in the background and
      // the user has granted permission. Best-effort: never prompt here, and
      // never let a failure surface.
      try {
        if (document.hidden && window.Notification && Notification.permission === 'granted') {
          navigator.serviceWorker.getRegistration().then(reg => {
            if (!reg) return
            reg.showNotification(row.project_name || 'Weldit', {
              body: `${who}: ${body}`,
              icon: '/img/icons/android-chrome-192x192.png',
              tag: `project-chat-${row.project_id}`,
              data: { projectId: row.project_id, threadId: row.thread_id }
            })
          }).catch(() => {})
        }
      } catch (e) { /* not supported — the snackbar already fired */ }
    },

    /* ---------------------------------------------------------------- */
    /* Realtime                                                          */
    /* ---------------------------------------------------------------- */

    /**
     * Subscribe to every thread the user belongs to, by id alone.
     *
     * Separate from pcnSubscribe() because that one is driven by the unread
     * rows, which only cover threads that ALREADY have something unread — so
     * subscribing from those alone means a conversation is first watched only
     * after a message in it has been missed. This joins the lot, so the very
     * first message in any thread arrives by socket.
     *
     * joinThread() also calls connect() internally, so this is what actually
     * opens the socket. Without it nothing would connect until unread appeared,
     * and the fallback timer would run forever.
     */
    pcnSubscribeThreadIds (ids) {
      if (!this.$projectChatRt || !Array.isArray(ids)) return

      ids.forEach(threadId => this.pcnJoin(threadId))
    },

    /**
     * Register this notifier's handler for one thread, once. joinThread() is
     * additive (Chat.vue registers its own handlers for the same threads), so
     * the guard here is what keeps a re-sync from stacking duplicate toasts.
     */
    pcnJoin (threadId) {
      if (!threadId || this.pcnOff[threadId]) return

      this.pcnOff[threadId] = this.$projectChatRt.joinThread(threadId, {
        created: payload => this.pcnOnRemoteCreated(payload)
      })
    },

    /**
     * A message arrived over the socket. Shared by both subscribe paths so a
     * thread joined by id notifies exactly like one joined from an unread row.
     */
    pcnOnRemoteCreated (payload) {
      const meta = this.pcnThreadMeta[payload.thread_id] || {}
      const msg = payload.message || {}

      if (!msg.id) return

      // Never our own message. The server already skips the sending socket
      // when socket_id was sent with the request; this covers the same user on
      // another device/tab, whose messages are not news to them either.
      const me = Number(this.userId || 0)
      if (me && msg.user && Number(msg.user.id) === me) return

      // Already on screen: Chat.vue is showing this very thread and the app is
      // in the foreground, so a toast would only duplicate what the user sees.
      // Both Chat.vue and this notifier now receive every event (handlers are
      // additive), which is what makes this check necessary.
      if (!document.hidden && this.$projectChatRt.isViewing(payload.thread_id)) return

      // A thread joined by id alone has no cached project meta until the first
      // seed/fetch fills it in. Fall back to a re-sync so the notification
      // still carries a project name rather than being dropped or shown blank.
      if (meta.project_id === undefined) {
        this.pcnPoll()
        return
      }

      // Never announce the same message twice, and never our own.
      if ((this.pcnSeen[meta.project_id] || 0) >= msg.id) return

      this.pcnSeen[meta.project_id] = msg.id
      this.pcnSaveSeen()

      this.pcnNotify({
        project_id: meta.project_id,
        project_name: meta.project_name || '',
        thread_id: payload.thread_id,
        user: msg.user || {},
        body: msg.body || ''
      })
    },

    /**
     * Subscribe to the threads the seed/poll has told us about.
     *
     * Adds the project meta a notification needs (name, id), which the id-only
     * path above cannot know. joinThread() is idempotent, so a thread already
     * joined by id is simply enriched here rather than joined twice.
     */
    pcnSubscribe (rows) {
      if (!this.$projectChatRt) return

      rows.forEach(row => {
        this.pcnThreadMeta[row.thread_id] = {
          project_id: row.project_id,
          project_name: row.project_name,
          is_private: row.is_private
        }

        this.pcnJoin(row.thread_id)
      })
    },

    /**
     * Let go of threads the server no longer lists for this user — removed
     * from a private conversation, thread muted, chat switched off for the
     * project. The list arrives with every unread payload, including the
     * unchanged short-circuit (the version tracks messages, not membership,
     * so this cannot wait for a "changed" one). Detaching the last handler
     * also clears any failure mark the realtime layer held for the thread:
     * that is what lets a 403 stop holding the fallback poll open as soon as
     * the server confirms the thread is not ours any more.
     */
    pcnPrune (ids) {
      const keep = {}
      ids.forEach(id => { keep[String(id)] = true })

      Object.keys(this.pcnOff).forEach(id => {
        if (keep[String(id)]) return
        try { this.pcnOff[id]() } catch (e) { /* ignore */ }
        delete this.pcnOff[id]
        delete this.pcnThreadMeta[id]
      })
    },

    /* ---------------------------------------------------------------- */
    /* Poll                                                              */
    /* ---------------------------------------------------------------- */

    pcnSocketLive () {
      return !!(this.$projectChatRt && this.$projectChatRt.isConnected())
    },

    /**
     * Apply one unread payload — from either source.
     *
     * Shared by the seed that rides along on check_and_get_tenant_features and
     * by the fallback fetch below, so both announce and subscribe identically.
     */
    pcnApplyPayload (data) {
      if (!data) return

      this.pcnLastPollAt = Date.now()

      // Chat switched off for the tenant (or the seed could not be built):
      // let go of everything and stay quiet. A payload without the flag comes
      // from a backend that predates it and is treated as enabled.
      if (data.enabled === false) {
        this.pcnDisable()
        return
      }
      this.pcnEnabled = true

      if (typeof data.version !== 'undefined') this.pcnVersion = data.version

      // Join every thread the user belongs to. Done BEFORE the unchanged check:
      // a payload with nothing unread still tells us which threads exist, and
      // that is exactly when the socket most needs opening — otherwise a user
      // with no unread messages never connects and polls forever.
      if (Array.isArray(data.threads)) {
        this.pcnSubscribeThreadIds(data.threads)
        this.pcnPrune(data.threads)
      }

      // Nothing moved — the server built no payload.
      if (data.unchanged) return

      const rows = data.projects || []
      this.pcnSubscribe(rows)

      rows.forEach(row => {
        const prev = this.pcnSeen[row.project_id] || 0
        if (this.pcnSeeded && row.last_id > prev) this.pcnNotify(row)
        if (row.last_id > prev) this.pcnSeen[row.project_id] = row.last_id
      })

      this.pcnSeeded = true
      this.pcnSaveSeen()
    },

    /**
     * Chat is off for this tenant: drop every subscription, close the socket
     * and mark the notifier dormant so no trigger issues a chat request again
     * until a seed says enabled.
     */
    pcnDisable () {
      this.pcnEnabled = false
      this.pcnVersion = null
      this.pcnPrune([])
      if (this.$projectChatRt) this.$projectChatRt.disconnect()
    },

    /**
     * Fetch the unread summary on demand.
     *
     * NOT called on start — the launch/reload seed comes free with
     * check_and_get_tenant_features. Called for a reconnect re-sync, for a
     * socket message whose thread has no project meta yet, and (throttled, via
     * pcnPollIfStale) when the dashboard is opened or the app is foregrounded.
     * Never before a seed has confirmed chat is on for this tenant.
     */
    pcnPoll () {
      if (document.hidden || !this.isAuthenticated || this.pcnEnabled !== true) return

      // Nudge the socket while we are here. connect() is a no-op once a socket
      // exists or the server said "disabled", but it re-attempts a config fetch
      // that failed transiently (offline start) and a socket that could not
      // open yet — and it subscribes the threads registered below once it
      // does. Without this, the one-registration-per-thread guard in pcnJoin()
      // would leave no code path that ever tries again.
      if (this.$projectChatRt && !this.pcnSocketLive() && Object.keys(this.pcnOff).length) {
        this.$projectChatRt.connect()
      }

      const v = this.pcnVersion === null ? '' : `?v=${encodeURIComponent(this.pcnVersion)}`

      this.pcnLastPollAt = Date.now()
      hostAppApi.get(`${this.getHostRefApi}project-chat/unread${v}`, { silenceErrors: true })
        .then(response => this.pcnApplyPayload(response.data || {}))
        .catch(() => {
          // Transient, or chat not enabled for this tenant. Nothing
          // user-visible should happen; the next trigger simply tries again.
        })
    },

    /**
     * Fetch only if nothing has been fetched or seeded for MIN_GAP_MS.
     *
     * For the "the user is looking again" triggers — dashboard opened, app back
     * in the foreground — which can fire in quick succession and must not turn
     * into a second request for the same answer.
     */
    pcnPollIfStale () {
      if (Date.now() - this.pcnLastPollAt < MIN_GAP_MS) return
      this.pcnPoll()
    },

    /**
     * Foreground again. A mobile browser may have dropped the socket without
     * pusher-js having noticed yet, so the connect() nudge re-opens one that
     * never came up (config fetch failed offline, no token at the time), and
     * one throttled catch-up fetch covers whatever was missed while hidden.
     * Hidden needs nothing: pcnPoll() already returns early while
     * document.hidden.
     */
    pcnOnVisibility () {
      if (document.hidden || !this.isAuthenticated || this.pcnEnabled !== true) return

      if (this.$projectChatRt) this.$projectChatRt.connect()
      this.pcnPollIfStale()
    },

    pcnStart () {
      this.pcnLoadSeen()

      // No fetch here on purpose. check_and_get_tenant_features already runs on
      // every launch and reload and carries the unread summary, which App.vue
      // hands to pcnApplyPayload(). That is the seed — issuing our own request
      // as well would just duplicate it. And no timer, ever: see the header.
      //
      // A reconnect may have missed events while the socket was down, so
      // re-sync when the transport comes up rather than waiting for the next
      // message — throttled, so the first connect right after the launch seed
      // (the common case) does not fetch the same answer again.
      if (this.$projectChatRt && !this.pcnOffTransport) {
        this.pcnOffTransport = this.$projectChatRt.onTransportChange(() => {
          if (this.pcnSocketLive()) this.pcnPollIfStale()
        })
      }

      document.addEventListener('visibilitychange', this.pcnOnVisibility)
    },

    pcnStop () {
      document.removeEventListener('visibilitychange', this.pcnOnVisibility)
      if (this.pcnOffTransport) {
        this.pcnOffTransport()
        this.pcnOffTransport = null
      }
      Object.keys(this.pcnOff).forEach(id => {
        try { this.pcnOff[id]() } catch (e) { /* ignore */ }
      })
      this.pcnOff = {}
      this.pcnThreadMeta = {}
      this.pcnEnabled = null
      this.pcnVersion = null
      if (this.$projectChatRt) this.$projectChatRt.disconnect()
    }
  },

  mounted () {
    if (this.isAuthenticated) this.pcnStart()
  },

  watch: {
    // Opening the dashboard is the one screen where "anything new?" is the
    // question being asked, so refresh there — throttled, and only once a
    // seed has confirmed chat is on: before that the launch seed is by
    // definition still on its way, so a fetch here would only duplicate it,
    // and on a tenant with chat off nothing must be fetched at all.
    '$route.path' (path) {
      if (path === '/dashboard' && this.isAuthenticated && this.pcnEnabled === true) this.pcnPollIfStale()
    },

    // Start once logged in, tear down on logout so the next user does not
    // inherit this one's channels or watermarks.
    isAuthenticated (value) {
      if (value) {
        this.pcnStart()
      } else {
        this.pcnStop()
        try { window.localStorage.removeItem(SEEN_KEY) } catch (e) { /* ignore */ }
      }
    }
  },

  beforeDestroy () {
    this.pcnStop()
  }
}
